import { formatDistanceToNow, differenceInHours } from 'date-fns';
import { es } from 'date-fns/locale';
import { RefreshCw, CheckCircle, AlertCircle, Clock, FileStack } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface SyncStatusProps {
  lastSyncAt: string | null;
  syncedForms: number;
  isLoading?: boolean;
}

type SyncState = 'recent' | 'stale' | 'old' | 'never';

function getSyncState(lastSyncAt: string | null): SyncState {
  if (!lastSyncAt) return 'never';

  const now = new Date();
  const syncDate = new Date(lastSyncAt);
  const hoursDiff = differenceInHours(now, syncDate);

  if (hoursDiff < 1) return 'recent';
  if (hoursDiff < 24) return 'stale';
  return 'old';
}

function getSyncStateConfig(state: SyncState) {
  switch (state) {
    case 'recent':
      return {
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        label: 'Sincronizado'
      };
    case 'stale':
      return {
        icon: Clock,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        label: 'Hace más de 1 hora'
      };
    case 'old':
      return {
        icon: AlertCircle,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        label: 'Desactualizado'
      };
    case 'never':
      return {
        icon: RefreshCw,
        color: 'text-gray-500',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        label: 'Sin sincronizar'
      };
  }
}

function SyncStatusSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-3 w-28" />
        </div>
      </div>
    </div>
  );
}

export function SyncStatus({ lastSyncAt, syncedForms, isLoading }: SyncStatusProps) {
  if (isLoading) {
    return <SyncStatusSkeleton />;
  }

  const syncState = getSyncState(lastSyncAt);
  const config = getSyncStateConfig(syncState);
  const Icon = config.icon;

  const timeAgo = lastSyncAt
    ? formatDistanceToNow(new Date(lastSyncAt), {
        addSuffix: true,
        locale: es
      })
    : 'Nunca';

  return (
    <div className="space-y-4">
      {/* Estado de sincronización */}
      <div className={`flex items-center gap-3 p-3 rounded-lg border ${config.bgColor} ${config.borderColor}`}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.bgColor}`}>
          <Icon className={`w-5 h-5 ${config.color}`} />
        </div>
        <div>
          <p className={`font-medium ${config.color}`}>{config.label}</p>
          <p className="text-sm text-muted-foreground">
            Última sincronización: {timeAgo}
          </p>
        </div>
      </div>

      {/* Formularios sincronizados */}
      <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <FileStack className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="font-medium">{syncedForms} formularios</p>
          <p className="text-sm text-muted-foreground">
            Conectados con JotForm
          </p>
        </div>
      </div>

      {/* Indicador visual */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span>&lt;1h</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-yellow-500" />
          <span>1-24h</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span>&gt;24h</span>
        </div>
      </div>
    </div>
  );
}
